/**
 * BookingPage Types
 * Shared types for the booking page components
 */

import { BookingFormData, PackageConfig, Addon, BookingSettings } from "@/types";

export interface AddressSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface TimeSlotOption {
  id: string;
  time: string;
}

export interface BookingFormErrors {
  serviceId?: string;
  packageId?: string;
  scheduledDate?: string;
  timeSlotId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  propertyAddress?: string;
  propertyType?: string;
  [key: string]: string | undefined;
}

export interface BookingFormTouched {
  customerName?: boolean;
  customerEmail?: boolean;
  customerPhone?: boolean;
  propertyAddress?: boolean;
  propertyType?: boolean;
  [key: string]: boolean | undefined;
}

export interface BookingState {
  step: number;
  formData: Partial<BookingFormData>;
  errors: BookingFormErrors;
  touched: BookingFormTouched;
  loading: boolean;
  showConfirmDialog: boolean;
  selectedPackage: PackageConfig | null;
  selectedAddons: Addon[];
}

export interface AddressAutocompleteState {
  query: string;
  suggestions: AddressSuggestion[];
  showSuggestions: boolean;
  loading: boolean;
  locating: boolean;
}

export interface TimeSlotState {
  slots: TimeSlotOption[];
  checking: boolean;
}

// Step component props
export interface StepProps {
  formData: Partial<BookingFormData>;
  errors: BookingFormErrors;
  touched: BookingFormTouched;
  settings: BookingSettings | null;
  onFormChange: (updates: Partial<BookingFormData>) => void;
  onFieldBlur: (fieldName: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
}

export interface ServicePackageStepProps extends StepProps {
  services: Array<{ id: string; title: string; order: number }>;
  addons: Addon[];
  selectedPackage: PackageConfig | null;
  selectedAddons: Addon[];
  onPackageSelect: (pkg: PackageConfig | null) => void;
  onAddonToggle: (addonId: string) => void;
}

export interface DateTimeStepProps extends StepProps {
  availableTimeSlots: TimeSlotOption[];
  checkingAvailability: boolean;
}

export interface ContactDetailsStepProps extends StepProps {
  addressQuery: string;
  addressSuggestions: AddressSuggestion[];
  showAddressSuggestions: boolean;
  addressLoading: boolean;
  locatingAddress: boolean;
  onAddressQueryChange: (query: string) => void;
  onAddressSelect: (suggestion: AddressSuggestion) => void;
  onUseMyLocation: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  user: { displayName?: string; email?: string; phoneNumber?: string } | null;
}

export const DAY_NAMES: Array<
  "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday"
> = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
